<script lang="ts">
  import { KanbanSquare, Search, Users, Layers, Boxes, Activity, FileText, LogOut, Sun, Moon, Monitor, BookOpen, FolderOpen, Download } from 'lucide-svelte';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { theme, type ThemeMode } from '$lib/state/theme.svelte';
  import { initials } from '$lib/utils/display';

  const {
    onLogout,
    open = false,
    onNavigate
  }: { onLogout: () => void; open?: boolean; onNavigate?: () => void } = $props();

  const isPipeline = $derived(route.current.tab === 'pipeline' || route.current.tab === 'quotations');

  // Shared by every nav button: navigate, then close the mobile drawer
  // (onNavigate is a no-op prop on desktop-sized viewports, where the
  // sidebar is never toggled in the first place — harmless to call always).
  function go(args: Parameters<typeof route.push>[0]) {
    route.push(args);
    onNavigate?.();
  }
  const isAdminUsers = $derived(route.current.tab === 'admin' && (route.current.sub ?? 'users') === 'users');
  const isAdminVerticals = $derived(route.current.tab === 'admin' && route.current.sub === 'verticals');
  const isAdminCatalog = $derived(route.current.tab === 'admin' && route.current.sub === 'catalog');
  const isAdminActivity = $derived(route.current.tab === 'admin' && route.current.sub === 'activity');
  const isAdminTermsTemplates = $derived(route.current.tab === 'admin' && route.current.sub === 'terms-templates');

  const THEME_OPTIONS: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
    { mode: 'light', label: 'Light theme', Icon: Sun },
    { mode: 'dark', label: 'Dark theme', Icon: Moon },
    { mode: 'system', label: 'Match system theme', Icon: Monitor }
  ];
</script>

<!-- Mobile (< lg): a fixed, off-canvas drawer that slides in over the
     content; backdrop closes it. At lg+ it reverts to a normal static
     flex-item sidebar (translate/position both reset), matching the
     original desktop layout exactly. -->
{#if open}
  <button
    type="button"
    class="fixed inset-0 z-30 bg-black/40 lg:hidden"
    onclick={onNavigate}
    aria-label="Close menu"
  ></button>
{/if}

<aside
  class="fixed inset-y-0 left-0 z-40 w-[252px] max-w-[85vw] shrink-0 bg-[var(--tym-white)] border-r border-toyama-cloudy-gray flex flex-col h-screen transition-transform duration-200 lg:static lg:translate-x-0 lg:z-auto {open
    ? 'translate-x-0'
    : '-translate-x-full'}"
>
  <div class="flex items-center gap-[11px] px-5 py-5 border-b border-toyama-cloudy-gray">
    <!-- Fixed navy (not the adaptive `bg-navy`, which lightens in dark mode)
         — the brand mark stays the same navy square in both themes. -->
    <div
      class="w-[34px] h-[34px] rounded-[9px] bg-[var(--tym-navy-800)] grid grid-cols-2 place-items-center gap-[2.5px] p-2 shrink-0"
    >
      <span class="w-1.5 h-1.5 rounded-[1.5px] bg-toyama-lime"></span>
      <span class="w-1.5 h-1.5 rounded-[1.5px] bg-toyama-lime"></span>
      <span class="w-1.5 h-1.5 rounded-[1.5px] bg-toyama-lime"></span>
      <span class="w-1.5 h-1.5 rounded-[1.5px] bg-toyama-lime"></span>
    </div>
    <div>
      <div class="font-heading font-bold text-[18px] text-navy tracking-tight leading-none">ProQuote</div>
      <div class="text-[10px] text-dark-gray tracking-[.06em] uppercase mt-[3px]">Toyama Controls</div>
    </div>
  </div>

  <nav class="flex-1 overflow-y-auto p-3 flex flex-col gap-0.5">
    <div class="text-[10px] font-semibold tracking-[.08em] uppercase text-dark-gray px-3 pt-1.5 pb-2">
      Workspace
    </div>
    {#if session.me?.user.role !== 'catalogue_manager'}
    <button
      type="button"
      class="pq-nav-item {isPipeline ? 'pq-nav-item-active' : ''}"
      onclick={() => go({ tab: 'pipeline' })}
    >
      <KanbanSquare class="h-[18px] w-[18px]" />
      Pipeline
    </button>
    <button type="button" class="pq-nav-item {route.current.tab === 'projects' ? 'pq-nav-item-active' : ''}" onclick={() => go({ tab: 'projects' })}>
      <FolderOpen class="h-[18px] w-[18px]" /> Projects
    </button>
    <button
      type="button"
      class="pq-nav-item {route.current.tab === 'search' ? 'pq-nav-item-active' : ''}"
      onclick={() => go({ tab: 'search' })}
    >
      <Search class="h-[18px] w-[18px]" />
      Search
    </button>
    {/if}
    <div class="text-[10px] font-semibold tracking-[.08em] uppercase text-dark-gray px-3 pt-4 pb-2">Catalogue</div>
    <button type="button" class="pq-nav-item {route.current.tab === 'catalogue' && (!route.current.sub || route.current.sub === 'products') ? 'pq-nav-item-active' : ''}" onclick={() => go({ tab: 'catalogue', sub: 'products' })}><Boxes class="h-[18px] w-[18px]" /> Products</button>
    <button type="button" class="pq-nav-item {route.current.tab === 'catalogue' && route.current.sub === 'datasheets' ? 'pq-nav-item-active' : ''}" onclick={() => go({ tab: 'catalogue', sub: 'datasheets' })}><BookOpen class="h-[18px] w-[18px]" /> Datasheets</button>
    <button type="button" class="pq-nav-item {route.current.tab === 'catalogue' && route.current.sub === 'marketing' ? 'pq-nav-item-active' : ''}" onclick={() => go({ tab: 'catalogue', sub: 'marketing' })}><FolderOpen class="h-[18px] w-[18px]" /> Marketing assets</button>
    <button type="button" class="pq-nav-item {route.current.tab === 'catalogue' && route.current.sub === 'price-lists' ? 'pq-nav-item-active' : ''}" onclick={() => go({ tab: 'catalogue', sub: 'price-lists' })}><Download class="h-[18px] w-[18px]" /> Price lists</button>

    {#if session.isAdmin}
      <div class="text-[10px] font-semibold tracking-[.08em] uppercase text-dark-gray px-3 pt-4 pb-2">
        Admin
      </div>
      <button
        type="button"
        class="pq-nav-item {isAdminUsers ? 'pq-nav-item-active' : ''}"
        onclick={() => go({ tab: 'admin', sub: 'users' })}
      >
        <Users class="h-[18px] w-[18px]" />
        Users
      </button>
      <button
        type="button"
        class="pq-nav-item {isAdminVerticals ? 'pq-nav-item-active' : ''}"
        onclick={() => go({ tab: 'admin', sub: 'verticals' })}
      >
        <Layers class="h-[18px] w-[18px]" />
        Verticals
      </button>
      <button
        type="button"
        class="pq-nav-item {isAdminCatalog ? 'pq-nav-item-active' : ''}"
        onclick={() => go({ tab: 'admin', sub: 'catalog' })}
      >
        <Boxes class="h-[18px] w-[18px]" />
        Catalog maintenance
      </button>
      <button
        type="button"
        class="pq-nav-item {isAdminActivity ? 'pq-nav-item-active' : ''}"
        onclick={() => go({ tab: 'admin', sub: 'activity' })}
      >
        <Activity class="h-[18px] w-[18px]" />
        Activity
      </button>
      <button
        type="button"
        class="pq-nav-item {isAdminTermsTemplates ? 'pq-nav-item-active' : ''}"
        onclick={() => go({ tab: 'admin', sub: 'terms-templates' })}
      >
        <FileText class="h-[18px] w-[18px]" />
        Terms Templates
      </button>
    {/if}
  </nav>

  <div class="border-t border-toyama-cloudy-gray p-3 flex items-center justify-center gap-1">
    {#each THEME_OPTIONS as opt (opt.mode)}
      <button
        type="button"
        class="flex-1 flex items-center justify-center rounded-md py-1.5 {theme.mode === opt.mode
          ? 'bg-[var(--tym-navy)] text-[var(--tym-white)]'
          : 'text-dark-gray hover:text-navy'}"
        onclick={() => theme.set(opt.mode)}
        aria-label={opt.label}
        aria-pressed={theme.mode === opt.mode}
        title={opt.label}
      >
        <opt.Icon class="h-4 w-4" />
      </button>
    {/each}
  </div>

  <div class="border-t border-toyama-cloudy-gray p-4 flex items-center gap-[11px]">
    <div
      class="w-9 h-9 rounded-full bg-[#226A73] text-white flex items-center justify-center font-semibold text-sm shrink-0"
    >
      {initials(session.me?.user.name || session.me?.user.email || '?')}
    </div>
    <div class="min-w-0 flex-1">
      <div class="text-[13px] font-medium text-navy truncate">
        {session.me?.user.name || session.me?.user.email}
      </div>
      <div class="text-[11px] text-dark-gray truncate capitalize">{session.me?.user.role === 'catalogue_manager' ? 'Catalogue manager' : session.me?.user.role}</div>
    </div>
    <button
      type="button"
      class="text-dark-gray hover:text-navy shrink-0"
      onclick={onLogout}
      aria-label="Log out"
      title="Log out"
    >
      <LogOut class="h-4 w-4" />
    </button>
  </div>
</aside>
